<template>
  <div>
    <p class="typography-body-sm-bold mt-2">
      <v-icon size="small">mdi-truck-flatbed</v-icon>
      {{
        appointment?.user?.company.name ||
        (appointmentType === 'drop'
          ? t('yard.messages.noDropCarrier')
          : t('yard.messages.noPickupCarrier'))
      }}
    </p>

    <div class="mb-4">
      <p>{{ assetContainerSpotLocation }}</p>

      <a
        href="#"
        @click="setCarrierSelectedSpot"
        :title="t('yard.pages.yardView.tooltips.goToSpot')"
        v-if="shouldShowGoToSpotLink">
        {{ t('yard.buttons.goToSpot') }}
      </a>
    </div>

    <div v-if="!appointment?.id">
      <p class="text-error mb-4">
        <small>
          <v-icon size="small">mdi-information-outline</v-icon>
          {{ t('yard.messages.noAppointmentAssociated') }}
        </small>
      </p>

      <yard-view-link-appointment-button
        :asset-visit="assetContainer"
        @open-create-appointment-dialog="isCreateAppointmentDialogVisible = true"
        @open-link-appointment-dialog="isLinkAppointmentDialogVisible = true" />
    </div>
    <div v-else>
      <div class="d-flex flex-column">
        <strong>
          <v-icon size="small">mdi-calendar</v-icon>
          {{
            appointmentType === 'drop'
              ? t('yard.messages.dropAppointment')
              : t('yard.messages.pickupAppointment')
          }}
        </strong>

        <span>{{ scheduledFor(appointment).date }}</span>
        <span>{{ scheduledFor(appointment).start }} - {{ scheduledFor(appointment).end }}</span>

        <p class="my-2">
          <v-chip :color="appointment.status.toLowerCase()" size="x-small" pill>
            {{ t(`yard.common.terms.${appointment.status}`).toUpperCase() }}
          </v-chip>
        </p>
        <a href="#" @click="emitViewAppointment">{{ t('yard.links.viewAppointment') }}</a>

        <v-btn
          variant="outlined"
          size="small"
          class="my-4"
          block
          @click="isUnlinkConfirmationVisible = true">
          <v-icon class="mr-1">mdi-link-off</v-icon>
          {{ t('global.basicTerms.unlink') }}
        </v-btn>
      </div>
    </div>

    <link-appointment-dialog
      :show-dialog="isLinkAppointmentDialogVisible"
      @close="isLinkAppointmentDialogVisible = false"
      @linked="handleAppointmentLinked"
      allow-link-to-arrived-appointments
      :follow-to-appointment-details="false"
      external-activator
      :reference-number-settings="selectedWarehouse.settings || {}"
      :warehouse="selectedWarehouse"
      :appointment-days-threshold="15"
      :asset-to-link="assetContainer"
      :appointment-type="appointmentType"
      :is-military-time-enabled="isMilitaryTimeEnabled" />

    <create-appointment-dialog
      :show-dialog="isCreateAppointmentDialogVisible"
      @close="isCreateAppointmentDialogVisible = false"
      @scheduled="handleNewAppointment"
      :follow-to-appointment-details="false"
      external-activator
      :context="{ selectedWarehouse }" />

    <confirm
      :should-show="isUnlinkConfirmationVisible"
      delete-confirmation
      show-delete-warning
      persistent
      @result="handleUnlinkConfirmation"
      :title="t('yard.messages.unlinkAppointment')"
      :entity-name="capitalize(appointmentType)"
      :button-true-text="t('global.basicTerms.yes').toUpperCase()"
      button-true-color="error"
      :width="650"></confirm>
  </div>
</template>
<script>
import { defineComponent, ref, computed } from 'vue';
import useYard from '@/modules/yard/composables/useYard';
import { capitalize } from 'lodash';
import { useNotify, useEventHub } from '@/composables';
import LinkAppointmentDialog from '@/components/elements/LinkAppointmentDialog.vue';
import CreateAppointmentDialog from '@/modules/appointments/components/CreateAppointmentDialog.vue';
import YardViewLinkAppointmentButton from '../YardViewLinkAppointmentButton.vue';
import { AppointmentStatus } from '@nova/core';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'YardViewAssetContainerAppointmentData',
  components: { LinkAppointmentDialog, CreateAppointmentDialog, YardViewLinkAppointmentButton },
  props: {
    appointment: {
      type: [Object, null],
      required: true
    },
    assetContainer: {
      type: Object,
      required: true
    },
    appointmentType: {
      type: String,
      required: true,
      validator: value => ['drop', 'pickup'].includes(value)
    }
  },
  setup(props) {
    const { t } = useI18n();
    const yardStore = useYard();
    const notify = useNotify();
    const eventHub = useEventHub();

    const isCreateAppointmentDialogVisible = ref(false);
    const isLinkAppointmentDialogVisible = ref(false);
    const isUnlinkConfirmationVisible = ref(false);

    const handleAppointmentLinked = async event => {
      const { appointment } = event;

      return handleNewAppointment(appointment, true);
    };

    const selfUpdate = async appointment => {
      if (yardStore.selectedSpotRef.value?.id) {
        return yardStore
          .fetchSpot(yardStore.selectedSpotRef.value.id)
          .then(yardStore.updateSpotOnSpotList);
      }

      if (yardStore.selectedAssetVisitRef.value?.id) {
        yardStore.selectedAssetVisitRef.value = {
          ...yardStore.selectedAssetVisitRef.value,
          [`${props.appointmentType}AppointmentId`]: appointment?.id,
          [`${props.appointmentType}Appointment`]: appointment
        };
      }
    };

    const handleNewAppointment = async (appointment, linked = false) => {
      try {
        yardStore.setIsLoadingAssetVisits(true);
        await axios.patch(`/asset-container/${props.assetContainer.id}`, {
          [`${props.appointmentType}AppointmentId`]: appointment.id
        });

        yardStore.trackMixpanelEvent(yardStore.MixPanelEvent.LINK_ASSET_CONTAINER_APPOINTMENT, {
          entryPoint: 'Live Yard View Panel',
          spot: yardStore.selectedSpotRef.value,
          metadata: {
            appointment_id: appointment.id,
            asset_container_id: props.assetContainer.id,
            asset_container_code: props.assetContainer.code,
            existing_appointment: linked,
            appointment_type: props.appointmentType
          }
        });

        notify(t('yard.messages.appointmentLinkedSuccessfully'), 'success');
        selfUpdate(appointment);
      } catch (error) {
        notify(t('yard.messages.failedToLinkTheAppointment'), 'error');
        console.error(error);
        return;
      } finally {
        isCreateAppointmentDialogVisible.value = false;
        yardStore.setIsLoadingAssetVisits(false);
      }
    };

    const handleUnlinkConfirmation = async result => {
      try {
        if (result) {
          yardStore.setIsLoadingAssetVisits(true);
          await axios.patch(`/asset-container/${props.assetContainer.id}`, {
            [`${props.appointmentType}AppointmentId`]: null
          });

          yardStore.trackMixpanelEvent(yardStore.MixPanelEvent.UNLINK_ASSET_CONTAINER_APPOINTMENT, {
            entryPoint: 'Live Yard View Panel',
            metadata: {
              asset_container_id: props.assetContainer.id,
              asset_container_code: props.assetContainer.code,
              appointment_type: props.appointmentType
            }
          });
          notify(t('yard.messages.appointmentUnlinkedSuccessfully'), 'success');
          selfUpdate(null);
        }
      } catch (error) {
        notify(t('yard.messages.failedToUnlinkTheAppointment'), 'error');
        console.error(error);
      } finally {
        isUnlinkConfirmationVisible.value = false;
        yardStore.setIsLoadingAssetVisits(false);
      }
    };

    const assetContainerPickupSpot = computed(
      () => yardStore.getAppointmentSpot(props.appointment?.id) ?? null
    );

    const assetContainerDropSpot = computed(
      () => yardStore.getAppointmentSpot(props.appointment?.id) ?? null
    );

    const assetContainerSpotLocation = computed(() => {
      const spot =
        props.appointmentType === 'pickup'
          ? assetContainerPickupSpot.value
          : assetContainerDropSpot.value;

      if (!spot) {
        if (
          yardStore.waitingAssetVisitsRef.value?.find(
            assetVisit =>
              Boolean(assetVisit.appointmentId) &&
              assetVisit.appointmentId === props.appointment?.id
          )
        ) {
          return t('yard.messages.waitingAssignment');
        }

        if (props.appointment?.status === AppointmentStatus.Completed) {
          return t('assetVisit.common.terms.departed');
        }

        return props.appointment?.status;
      }

      if (spot.id === yardStore.selectedSpotRef.value?.id) {
        return t('yard.messages.atThisSpot');
      }

      return `${spot.spotArea.name} - ${spot.code}`;
    });

    const setCarrierSelectedSpot = () => {
      if (assetContainerPickupSpot.value) {
        yardStore.selectedSpotRef.value = assetContainerPickupSpot.value;
      }
    };

    const shouldShowGoToSpotLink = computed(() => {
      if (props.appointmentType === 'pickup') {
        return (
          Boolean(assetContainerPickupSpot.value?.id) &&
          assetContainerPickupSpot.value?.id !== yardStore.selectedSpotRef.value?.id
        );
      }

      if (props.appointmentType === 'drop') {
        return (
          Boolean(assetContainerDropSpot.value?.id) &&
          assetContainerDropSpot.value?.id !== yardStore.selectedSpotRef.value?.id
        );
      }

      return false;
    });

    return {
      t,
      isUnlinkConfirmationVisible,
      assetContainerSpotLocation,
      shouldShowGoToSpotLink,
      handleUnlinkConfirmation,
      isCreateAppointmentDialogVisible,
      isLinkAppointmentDialogVisible,
      selectedWarehouse: yardStore.selectedWarehouseRef,
      scheduledFor: yardStore.scheduledFor,
      handleAppointmentLinked,
      handleNewAppointment,
      setCarrierSelectedSpot,
      capitalize,
      emitViewAppointment: () =>
        eventHub.emit('yard-view-appointment', {
          appointmentId: props.appointment?.id,
          assetVisitId: null
        }),
      eventHub,
      isMilitaryTimeEnabled: yardStore.isMilitaryTimeEnabled
    };
  }
});
</script>
